/**
 * Container for the Sell action modal.
 *
 * Manages sale price draft state. On confirm, delegates to the parent
 * callback with the final sale price value. Supports edit mode with
 * a "Cancel Sale" action to remove the assignment. The sale terms also feed
 * the Live Impact verdict as they are typed (SE-7002).
 * @module features/squad-planning/components/SellActionModal
 */

import { type Href, useRouter } from "expo-router";
import React, { memo, useCallback, useMemo, useState } from "react";

import { POSTHOG_FLAGS } from "@/config/featureFlags";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import { buildComplianceHref } from "@/features/financial-compliance/navigation/complianceRoute";

import { useLiveImpact } from "../../../hooks/useLiveImpact";

import SellActionModalView from "./SellActionModalView";

import type { ActionTerms } from "@/features/financial-compliance/domain/actionFinancials";

/** Props for {@link SellActionModalContainer} */
export interface SellActionModalProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Called to close the modal */
  readonly onClose: () => void;
  /** Called with the sale price on confirm */
  readonly onConfirm: (salePrice: number | null) => void;
  /** Called to remove the action (edit mode only) */
  readonly onCancelAction?: () => void;
  /** Player name for the title */
  readonly playerName: string;
  /**
   * Player the action applies to.
   *
   * Optional so no mount point breaks by omitting it; the Live Impact verdict
   * uses it to avoid double-counting a player who is already staged.
   */
  readonly playerId?: string | null;
  /** Initial sale price value (for edit mode) */
  readonly initialValue: number | null;
  /** Whether the modal is in edit mode (existing action) */
  readonly isEditMode?: boolean;
}

/**
 * Container managing Sell action modal state.
 * @param root0 - Component props
 * @param root0.isOpen - Whether modal is visible
 * @param root0.onClose - Close handler
 * @param root0.onConfirm - Confirm handler with sale price
 * @param root0.onCancelAction - Remove action handler (edit mode)
 * @param root0.playerName - Player name for display
 * @param root0.playerId - Player the action applies to
 * @param root0.initialValue - Initial sale price for edit mode
 * @param root0.isEditMode - Whether in edit mode
 */
const SellActionModalContainer = ({
  isOpen,
  onClose,
  onConfirm,
  onCancelAction,
  playerName,
  playerId = null,
  initialValue,
  isEditMode = false,
}: SellActionModalProps) => {
  const [salePrice, setSalePrice] = useState<number | null>(initialValue);
  const router = useRouter();

  const terms = useMemo<ActionTerms>(
    () => ({ kind: "Sell", salePrice }),
    [salePrice]
  );
  const { model: liveImpact, scenarioId } = useLiveImpact(terms, playerId);
  // SE-8003: the Compliance tab is unreachable with the flag off, so this
  // link must not navigate into it. `=== true` is the fail-safe idiom — an
  // unconfigured flag, a still-loading PostHog client, or any non-`true`
  // value all skip the navigation, never perform it.
  const isComplianceEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.SQUAD_FINANCIALS_TAB) === true;

  const handleViewFullImpact = useCallback(() => {
    onClose();
    if (isComplianceEnabled) {
      router.push(buildComplianceHref(scenarioId) as Href);
    }
  }, [router, scenarioId, onClose, isComplianceEnabled]);

  const handleSalePriceChange = useCallback((value: number | null) => {
    setSalePrice(value);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(salePrice);
  }, [salePrice, onConfirm]);

  if (!isOpen) return null;

  return (
    <SellActionModalView
      isOpen={isOpen}
      playerName={playerName}
      salePrice={salePrice}
      onSalePriceChange={handleSalePriceChange}
      onConfirm={handleConfirm}
      onCancel={onClose}
      onCancelAction={onCancelAction}
      isEditMode={isEditMode}
      liveImpact={liveImpact}
      onViewFullImpact={handleViewFullImpact}
    />
  );
};

SellActionModalContainer.displayName = "SellActionModalContainer";

export default memo(SellActionModalContainer);
